import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Video, Sparkles, Download, Trash2, Lock, Clock, Zap } from "lucide-react";
import { useTr } from "@/i18n/translator";
import BrandSelector from "@/components/BrandSelector";

// Sora 2 oficialmente solo acepta 720x1280 (9:16) y 1280x720 (16:9).
// El formato 1:1 (cuadrado) no está soportado por el modelo, así que lo omitimos.
const ASPECTS = [
  { id: "9:16", label: "9:16 · Reels / TikTok / Shorts" },
  { id: "16:9", label: "16:9 · YouTube / Landscape" },
];
const DURATIONS = [4, 8, 12];
const FREQUENCIES = [
  { id: "daily", label: "Diario", days: 1 },
  { id: "weekly", label: "Semanal", days: 7 },
  { id: "biweekly", label: "Cada 15 días", days: 14 },
  { id: "custom", label: "Personalizado", days: null },
];
const MODELS = [
  { id: "sora-2", label: "IA · Rápido" },
  { id: "sora-2-pro", label: "IA · Alta calidad" },
];

export default function Videos() {
  const tr = useTr();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [videos, setVideos] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [aspect, setAspect] = useState("9:16");
  const [duration, setDuration] = useState(4);
  const [model, setModel] = useState("sora-2");
  const [autoPackage, setAutoPackage] = useState(true);
  const [loading, setLoading] = useState(false);
  const [brandId, setBrandId] = useState("");
  const pollTimerRef = useRef(null);

  const loadAll = async () => {
    const [s, v] = await Promise.all([
      api.get("/videos-settings"),
      api.get("/videos"),
    ]);
    setSettings(s.data);
    setVideos(v.data);
  };

  useEffect(() => {
    loadAll();
    return () => { if (pollTimerRef.current) clearInterval(pollTimerRef.current); };
  }, []);

  // Poll any processing video every 10s
  useEffect(() => {
    const processing = videos.filter((v) => v.status === "processing");
    if (processing.length === 0) {
      if (pollTimerRef.current) { clearInterval(pollTimerRef.current); pollTimerRef.current = null; }
      return;
    }
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/videos");
        setVideos(data);
      } catch {}
    }, 10000);
  }, [videos]);

  const generate = async () => {
    if (!prompt.trim() || prompt.trim().length < 5) {
      toast.error(tr("Describe tu video con al menos 5 caracteres"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/generate/video", { prompt, aspect, duration, model, auto_package: autoPackage, brand_id: brandId || null });
      toast.success("Video en cola — tarda 2-5 min");
      setPrompt("");
      loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar video");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/videos/${id}`);
    loadAll();
  };

  const hasAddon = settings?.has_video_addon;

  if (!settings) return <div className="font-mono-mark text-xs text-[#5C5C5C]">CARGANDO…</div>;

  return (
    <div className="space-y-6" data-testid="videos-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">OPERATIVO · VIDEO IA · 9:16</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Generador de videos IA")}</h1>
        </div>
        {hasAddon && (
          <div className="bg-[#FFD600] border border-[#0A0A0A] px-4 py-3" data-testid="video-quota">
            <div className="font-mono-mark text-[10px]">HOY · {settings.today_used} / 1</div>
            <div className="font-mono-data text-sm mt-1">Frecuencia: <b>{settings.video_frequency.toUpperCase()}</b>{settings.video_custom_days ? ` · ${settings.video_custom_days}d` : ""}</div>
          </div>
        )}
      </div>

      {!hasAddon ? (
        <div className="bg-white border border-[#0A0A0A] p-10 text-center" data-testid="video-locked">
          <div className="w-14 h-14 bg-[#0A0A0A] text-[#FFD600] flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6" />
          </div>
          <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("ADD-ON BLOQUEADO")}</div>
          <h2 className="font-display text-3xl mt-3">{tr("Activa el módulo de Video IA")}</h2>
          <p className="font-mono-data text-sm text-[#5C5C5C] mt-3 max-w-xl mx-auto">
            Los videos con IA requieren el add-on de Video (2x el precio de tu plan). Genera hasta 1 video diario
            con calidad premium, vertical 9:16 listo para Reels/TikTok/Shorts.
          </p>
          <Link to="/dashboard/subscription" className="mt-6 inline-flex items-center gap-2 bg-[#E53935] text-white px-6 py-3 font-mono-mark text-xs border border-[#E53935] hover-brutal" data-testid="video-unlock-cta">
            <Zap className="w-4 h-4" /> {tr("ACTIVAR ADD-ON DE VIDEO")}
          </Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Form */}
          <div className="lg:col-span-2 bg-white border border-[#0A0A0A] p-6 space-y-5">
            <div>
              <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-2">FORMATO</div>
              <div className="space-y-2">
                {ASPECTS.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => setAspect(a.id)}
                    className={`w-full text-left px-3 py-2 font-mono-mark text-[10px] border ${aspect === a.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#0A0A0A]"}`}
                    data-testid={`video-aspect-${a.id.replace(":", "x")}`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-2">{tr("DURACIÓN")}</div>
              <div className="grid grid-cols-3 gap-2">
                {DURATIONS.map((d) => (
                  <button key={d} onClick={() => setDuration(d)} className={`py-2 font-mono-mark text-[10px] border ${duration === d ? "bg-[#E53935] text-white border-[#E53935]" : "bg-white border-[#0A0A0A]"}`} data-testid={`video-dur-${d}`}>
                    {d}s
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div className="font-mono-mark text-[10px] text-[#5C5C5C] mb-2">MODELO</div>
              <div className="space-y-2">
                {MODELS.map((m) => (
                  <button key={m.id} onClick={() => setModel(m.id)} className={`w-full text-left px-3 py-2 font-mono-mark text-[10px] border ${model === m.id ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#0A0A0A]"}`} data-testid={`video-model-${m.id}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <BrandSelector value={brandId} onChange={setBrandId} testid="video-brand" />

            <label className="block">
              <span className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("PROMPT · DESCRIBE EL VIDEO")}</span>
              <textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={5}
                placeholder={tr("Ej: Barista sirviendo café especialidad en CDMX, cámara lenta, luz cálida, enfoque suave en el vapor del café.")}
                className="w-full mt-2 bg-white border border-[#0A0A0A] px-4 py-3 font-mono-data text-sm focus:outline-none focus:ring-2 focus:ring-[#E53935]"
                data-testid="video-prompt"
              />
            </label>

            {/* Auto-package toggle */}
            <button
              onClick={() => setAutoPackage(!autoPackage)}
              className={`w-full text-left border p-3 ${autoPackage ? "border-[#E53935] bg-[#FFD600]" : "border-[#0A0A0A] bg-white"}`}
              data-testid="video-auto-package"
            >
              <div className="flex items-center justify-between">
                <span className="font-mono-mark text-[10px]">ORQUESTADOR VIDEO → POST</span>
                <span className={`font-mono-mark text-[10px] px-2 py-0.5 ${autoPackage ? "bg-[#E53935] text-white" : "bg-[#E5E5E5]"}`}>
                  {autoPackage ? "ACTIVO" : "INACTIVO"}
                </span>
              </div>
              <div className="font-mono-data text-xs mt-1">
                {tr("La IA crea además el caption de Instagram, hook de TikTok, tweet y hashtags — listos para publicar.")}
              </div>
            </button>

            <button
              onClick={generate}
              disabled={loading || settings.today_used >= 1}
              className="w-full bg-[#0A0A0A] text-white px-4 py-3 font-mono-mark text-xs hover-brutal border border-[#0A0A0A] flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="video-generate"
            >
              <Sparkles className="w-4 h-4" />
              {loading ? "ENVIANDO…" : settings.today_used >= 1 ? "LÍMITE DE HOY ALCANZADO" : "GENERAR VIDEO"}
            </button>
            <p className="font-mono-mark text-[10px] text-[#5C5C5C] text-center">
              {tr("La IA tarda aproximadamente 2–5 min. Te avisamos cuando esté listo.")}
            </p>
          </div>

          {/* Library */}
          <div className="lg:col-span-3 space-y-4">
            {videos.length === 0 ? (
              <div className="bg-white border border-dashed border-[#0A0A0A] p-10 text-center">
                <Video className="w-8 h-8 mx-auto text-[#5C5C5C]" />
                <div className="font-mono-mark text-[10px] text-[#5C5C5C] mt-3">{tr("BIBLIOTECA VACÍA")}</div>
                <div className="text-sm mt-1">{tr("Genera tu primer video con IA.")}</div>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {videos.map((v) => <VideoCard key={v.id} video={v} onDelete={() => remove(v.id)} />)}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function authedVideoUrl(url) {
  if (!url) return null;
  const token = localStorage.getItem("cia_token");
  // El elemento <video> del navegador no envía headers Authorization, por lo
  // que añadimos el JWT al query string. El endpoint backend acepta `?t=`.
  const sep = url.includes("?") ? "&" : "?";
  return token ? `${url}${sep}t=${encodeURIComponent(token)}` : url;
}

function VideoCard({ video, onDelete }) {
  const tr = useTr();
  const playableUrl = authedVideoUrl(video.url);
  return (
    <div className="bg-white border border-[#0A0A0A] overflow-hidden" data-testid={`video-card-${video.id}`}>
      {video.status === "ready" && playableUrl ? (
        <video src={playableUrl} controls className="w-full aspect-[9/16] bg-black max-h-[400px] object-contain" />
      ) : video.status === "processing" ? (
        <div className="w-full aspect-[9/16] bg-[#0A0A0A] flex flex-col items-center justify-center text-white max-h-[400px]">
          <div className="w-10 h-10 border-4 border-[#FFD600] border-t-[#E53935] rounded-full animate-spin" />
          <div className="font-mono-mark text-[10px] mt-4 text-[#FFD600]">PROCESANDO</div>
          <div className="font-mono-data text-xs mt-1 text-white/60 flex items-center gap-1">
            <Clock className="w-3 h-3" /> 5–30 min
          </div>
          <div className="font-mono-data text-[10px] mt-2 text-white/40 px-4 text-center max-w-[220px]">
            Sora 2 está en cola. No cierres ni recargues — el video aparecerá aquí cuando esté listo.
          </div>
        </div>
      ) : (
        <div className="w-full aspect-[9/16] bg-[#E53935] flex flex-col items-center justify-center text-white max-h-[400px]">
          <div className="font-mono-mark text-[10px]">FALLIDO</div>
          <div className="font-mono-data text-xs mt-2 px-4 text-center break-words">{video.error || "Error desconocido"}</div>
        </div>
      )}
      <div className="p-3 border-t border-[#0A0A0A]">
        <div className="flex items-center justify-between">
          <span className={`font-mono-mark text-[9px] px-2 py-1 ${
            video.status === "ready" ? "bg-[#0A0A0A] text-[#FFD600]"
            : video.status === "processing" ? "bg-[#FFD600]"
            : "bg-[#E53935] text-white"
          }`}>
            {video.status.toUpperCase()} · {video.aspect} · {video.duration}s
          </span>
          <span className="font-mono-mark text-[9px] text-[#5C5C5C]">{new Date(video.created_at).toLocaleString("es-MX")}</span>
        </div>
        <p className="text-xs mt-2 line-clamp-2">{video.prompt}</p>

        {video.package && (
          <div className="mt-3 border-t border-[#0A0A0A] pt-3 space-y-2" data-testid={`video-package-${video.id}`}>
            <div className="font-mono-mark text-[9px] text-[#E53935]">{tr("PAQUETE COMPLETO · LISTO PARA PUBLICAR")}</div>
            {video.package.instagram_caption && (
              <PackageBlock label="INSTAGRAM" text={video.package.instagram_caption} video={video} network="instagram" hashtags={video.package.hashtags} />
            )}
            {video.package.tiktok_hook && (
              <PackageBlock label="TIKTOK · HOOK" text={video.package.tiktok_hook} video={video} network="tiktok" hashtags={video.package.hashtags} />
            )}
            {video.package.twitter_post && (
              <PackageBlock label="TWITTER / X" text={video.package.twitter_post} video={video} network="twitter" hashtags={video.package.hashtags} />
            )}
            {video.package.hashtags?.length > 0 && (
              <div className="font-mono-data text-xs text-[#E53935]">{video.package.hashtags.join(" ")}</div>
            )}
          </div>
        )}

        <div className="flex gap-2 mt-3">
          {video.status === "ready" && playableUrl && (
            <a href={playableUrl} download className="font-mono-mark text-[9px] px-2 py-1 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white flex items-center gap-1" data-testid={`video-download-${video.id}`}>
              <Download className="w-3 h-3" /> {tr("DESCARGAR")}
            </a>
          )}
          <button onClick={onDelete} className="font-mono-mark text-[9px] px-2 py-1 border border-[#E53935] text-[#E53935] hover:bg-[#E53935] hover:text-white flex items-center gap-1" data-testid={`video-delete-${video.id}`}>
            <Trash2 className="w-3 h-3" /> {tr("ELIMINAR")}
          </button>
        </div>
      </div>
    </div>
  );
}

function PackageBlock({ label, text, video, network, hashtags }) {
  const tr = useTr();
  const [scheduling, setScheduling] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado");
  };
  const schedule = async () => {
    if (!video || !network) return;
    setScheduling(true);
    try {
      await api.post("/posts", {
        body: text,
        network,
        hashtags: hashtags || [],
        video_url: video.url,
        video_id: video.id,
        status: "pending",
        scheduled_at: null,
      });
      toast.success(`Post creado en ${network.toUpperCase()} · revisa el calendario`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el post");
    } finally {
      setScheduling(false);
    }
  };
  return (
    <div className="bg-[#F4F4F0] border border-[#0A0A0A] p-2">
      <div className="flex items-center justify-between">
        <span className="font-mono-mark text-[8px] text-[#5C5C5C]">{label}</span>
        <div className="flex gap-2">
          <button onClick={copy} className="font-mono-mark text-[8px] underline decoration-[#E53935]">{tr("COPIAR")}</button>
          {video && network && (
            <button
              onClick={schedule}
              disabled={scheduling}
              className="font-mono-mark text-[8px] bg-[#E53935] text-white px-2 py-0.5 border border-[#E53935] disabled:opacity-50"
              data-testid={`video-publish-${network}-${video.id}`}
            >
              {scheduling ? "…" : "+ POST"}
            </button>
          )}
        </div>
      </div>
      <p className="font-mono-data text-[11px] mt-1 line-clamp-4 whitespace-pre-wrap">{text}</p>
    </div>
  );
}
